import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

import { Icon, Image, NavbarRoute, LogOutButton } from "components/atoms";

import { NavRoute } from "models/routes";
import { IconName } from "interfaces/icons";

import { navRoutes } from "utils/routes";

import { SIZES } from "styles/size";
import { MAIN_THEME } from "styles/colors";

import logoSm from "assets/images/logoSm.svg";
import logo from "assets/images/logo.svg";

interface Props {
	isExpanded: boolean;
	toggle: () => void;
}
interface ContainerProps {
	expanded: boolean;
}

interface TogglerProps {
	expanded: boolean;
}

const Container = styled.div<ContainerProps>`
	height: 100vh;
	width: ${(props) =>
		props.expanded
			? `${SIZES.EXPANDED_NAVBAR_WIDTH}px`
			: `${SIZES.NAVBAR_WIDTH}px`};
	padding: 70px 10px 10px;
	background: ${MAIN_THEME.DARK_BG_SECONDARY};
	z-index: 100;
	transition: width 0.25s ease;
	position: relative;
	display: flex;
	flex-direction: column;
	order: 1;

	@media only screen and (max-height: 860px) {
		padding: 45px 10px 10px;
	}

	@media (max-width: 768px) {
		height: 70px;
		width: 100%;
		padding: 0 10px;
		flex-direction: row;
		align-items: center;
		justify-content: space-evenly;
		border-right: none;
		border-top: 1px solid ${MAIN_THEME.BORDER_COLOR};
		box-shadow: inset 0 1px 0 ${MAIN_THEME.BORDER_COLOR_LIGHT};
		overflow-x: auto;
		overflow-y: hidden;
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		gap: 0;
		order: 3;
	}

	@media (max-width: 480px) {
		height: 60px;
		padding: 0 5px;
	}
`;

const Toggler = styled.button<TogglerProps>`
	width: 45px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	outline: none;
	position: absolute;
	right: 0;
	top: 65%;
	transform: ${(props) =>
		props.expanded ? "translateY(-65%)" : "translateY(-65%) rotate(180deg)"};
	cursor: pointer;

	@media (max-width: 768px) {
		display: none;
	}
`;

const LogoWrap = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	margin-bottom: 8vh;
	height: 44px;

	@media only screen and (max-height: 860px) {
		margin-bottom: 5vh;
	}

	@media (max-width: 768px) {
		display: none;
	}
`;

const LogOutButtonWrap = styled.div`
	margin-top: auto;
	width: 100%;

	@media (max-width: 768px) {
		margin-top: 0;
		margin-left: 0;
		width: auto;
		flex: 1;
		display: flex;
		justify-content: center;
	}
`;

const ChatButton = styled.button`
	background: transparent;
	border: none;
	display: none;
	align-items: center;
	justify-content: center;
	flex: 1;
	cursor: pointer;

	@media (max-width: 768px) {
		display: flex;
	}
`;

const Navbar = (props: Props): JSX.Element => {
	const location = useLocation();

	const { isExpanded, toggle } = props;

	// Close Trengo widget when resizing from mobile to web view
	useEffect(() => {
		const handleResize = () => {
			// If window width is > 768px (web view), close the widget
			if (window.innerWidth > 768 && window.Trengo?.eventBus) {
				window.Trengo.eventBus.$emit("widget.close");
			}
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const handleChatClick = () => {
		// Toggle Trengo widget open/close on mobile
		if (window.Trengo?.eventBus) {
			const isWidgetOpen = Boolean(
				document.querySelector("iframe.trengo-vue-iframe.visible")
			);
			window.Trengo.eventBus.$emit(isWidgetOpen ? "widget.close" : "widget.open");
		}
	};

	const renderRoutes = () =>
		navRoutes.map((route: NavRoute) => {
			const isActive = location.pathname === route.to;

			return (
				<NavbarRoute
					route={route}
					key={route.to}
					isActive={isActive}
					isExpanded={isExpanded}
				/>
			);
		});

	return (
		<Container expanded={isExpanded}>
			<LogoWrap>
				<Image
					src={isExpanded ? logo : logoSm}
					width={
						isExpanded
							? SIZES.EXPANDED_NAVBAR_LOGO_WIDTH
							: SIZES.NAVBAR_LOGO_WIDTH
					}
				/>
			</LogoWrap>
			<Toggler onClick={toggle} expanded={isExpanded}>
				<Icon name={IconName.NAVBAR_TOGGLER} color="#ffffff" size={36} />
			</Toggler>
			{renderRoutes()}
			<ChatButton onClick={handleChatClick}>
				<Icon name={IconName.CHAT} color={MAIN_THEME.TEXT_TERTIARY} size={24} />
			</ChatButton>
			<LogOutButtonWrap>
				<LogOutButton isExpanded={isExpanded} />
			</LogOutButtonWrap>
		</Container>
	);
};

export default Navbar;
